import { FC, useState, useEffect } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { useAuth } from '../../../../../auth';
import { successToast, errorToast } from '../../../../../../../_metronic/helpers/components/Toaster';

interface WhatsappDocumentsProps {
    selectedDocs: any[];
    setSelectedDocs: (docs: any[]) => void;
    orderId?: string;
}

const WhatsappDocuments: FC<WhatsappDocumentsProps> = ({ selectedDocs, setSelectedDocs, orderId, }) => {
    const { currentUser } = useAuth();
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
    const [manualRecipients, setManualRecipients] = useState([{ mobile: '', name: '' }]);
    const [isSending, setIsSending] = useState(false);
    const [allUsers, setAllUsers] = useState<any[]>([]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BASE_API_URL}users?status=ACTIVE&type=PICKUP_POINT_STAFF`);
                if (response.data) {
                    const options = response.data.map((u: any) => ({
                        label: u.personalDetails?.name || 'Unknown',
                        value: u.personalDetails?.mobile || '',
                    })).filter((o: any) => o.value);
                    setAllUsers(options);
                }
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        fetchUsers();
    }, []);

    const handleWhatsappSend = async () => {
        setIsSending(true);
        try {
            const users = [
                ...selectedUsers.map(u => ({
                    mobile: u.value,
                    customerName: u.label
                })),
                ...manualRecipients
                    .filter(r => r.mobile && r.name)
                    .map(r => ({ mobile: r.mobile, customerName: r.name }))
            ];

            if (users.length === 0) {
                errorToast('Please select at least one contact or enter manual details.');
                setIsSending(false);
                return;
            }

            if (selectedDocs.length === 0) {
                errorToast('Please select at least one document to send.');
                setIsSending(false);
                return;
            }

            const payload = {
                senderId: currentUser?.personalDetails?.name,
                users,
                documents: selectedDocs.map(doc => ({
                    url: doc.docUrl,
                    fileName: (orderId ? orderId + "-" : "") + (doc.docType || 'Document')
                }))
            };

            await axios.post(`${process.env.REACT_APP_BASE_API_URL}whatsapp/send`, payload);
            successToast('WhatsApp message sent successfully!');
            setSelectedUsers([]);
            setManualRecipients([{ mobile: '', name: '' }]);
            setSelectedDocs([]);
        } catch (error) {
            console.error('Error sending WhatsApp message:', error);
            errorToast('Failed to send WhatsApp message.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="whatsapp-section p-5 bg-light rounded shadow-sm mb-5">
            <h3 className="mb-4 d-flex align-items-center">
                <i className="bi bi-whatsapp text-success fs-2 me-2"></i>
                Send via WhatsApp
            </h3>

            <div className="row g-3">
                <div className="col-12">
                    <label className="form-label fw-bold">Select Contacts</label>
                    <Select
                        isMulti
                        options={allUsers}
                        value={selectedUsers}
                        onChange={(selected) => setSelectedUsers(selected as any)}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="Select from existing contacts..."
                    />
                </div>

                {manualRecipients.map((recipient, index) => (
                    <div key={index} className="row g-3 mb-3 align-items-end">
                        <div className="col-md-5">
                            <label className="form-label fw-bold small text-muted">Recipient Mobile ({index + 1})</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="91XXXXXXXXXX"
                                value={recipient.mobile}
                                onChange={(e) => {
                                    const updated = [...manualRecipients];
                                    updated[index].mobile = e.target.value;
                                    setManualRecipients(updated);
                                }}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label fw-bold small text-muted">Recipient Name ({index + 1})</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter name"
                                value={recipient.name}
                                onChange={(e) => {
                                    const updated = [...manualRecipients];
                                    updated[index].name = e.target.value;
                                    setManualRecipients(updated);
                                }}
                            />
                        </div>
                        <div className="col-md-2">
                            {manualRecipients.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-icon btn-light-danger w-100"
                                    onClick={() => setManualRecipients(manualRecipients.filter((_, i) => i !== index))}
                                    title="Remove"
                                >
                                    <i className="bi bi-trash fs-3"></i>
                                </button>
                            )}
                        </div>
                    </div>
                ))}

                <div className="col-12 mt-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-light-primary"
                        onClick={() => setManualRecipients([...manualRecipients, { mobile: '', name: '' }])}
                    >
                        <i className="bi bi-plus fs-3"></i> Add More
                    </button>
                </div>

                <div className="col-12 text-end mt-4">
                    <div className="mb-3 text-start small text-muted">
                        {selectedDocs.length} document(s) selected
                    </div>
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleWhatsappSend}
                        disabled={isSending}
                        style={{
                            backgroundColor: '#25D366',
                            borderColor: '#25D366',
                            color: 'white',
                            padding: '10px 20px',
                            fontWeight: 'bold'
                        }}
                    >
                        {isSending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Sending...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-whatsapp me-2"></i>
                                Whatsapp send
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WhatsappDocuments;
